import React from 'react';
import { Modal, ModalBody } from 'reactstrap';
import './exam-prep-kit-v2.scss';
import SharedProfile from '../../../shared-storage/profile';
import Path from '../routes/routes-path';
import ExamPrepKitV2Api from '../../apis/exam-prep-kit-v2';
import TableOfContent from './table-of-contents/table-of-contents';
import { Constants } from '../../constants/constants';
import Loading from '../common/loading/loading';
import LayoutShared from '../common/layout-shared/layout-shared';

/**

 * ExamPrepKitV2
 * ─────────────
 * 3-step Exam Prep Kit UI (vertical timeline layout):
 *   Step 1 – Baseline Assessment
 *   Step 2 – Revise & Learn
 *   Step 3 – FinishLine Assessment
 *
 * Props:
 *   delegate  – parent component (must expose hideExamPrepKitV2())
 *   meta      – chapter/context meta from ChapterActivityList
 */
class ExamPrepKitV2 extends React.Component {

    constructor(props) {
        super(props);
        this.userInfo = SharedProfile.getProfile() || {};
        this.layoutShared = LayoutShared.getLayout();
        this.meta = this.props.meta || this.props.metaInfo || {};
        if (this.meta.subjectId && !this.userInfo.subjectId) {
            this.userInfo.subjectId = this.meta.subjectId;
        } else if (!this.userInfo.subjectId) {
            this.userInfo.subjectId = Constants.MATHS_SUBJECTID;
        }
        if (!this.meta.subjectId) {
            this.meta.subjectId = this.userInfo.subjectId;
        }
        if (this.meta.title && !this.meta.chapterTitle) {
            this.meta.chapterTitle = this.meta.title;
        }
        if (!this.meta.chapterKey && this.meta.key) {
            this.meta.chapterKey = this.meta.key;
        }
        this.state = {
            baselineStatus: null,
            finishlineStatus: null,
            reviseProgress: 0,
            isLoading: true,
            showModal: false,
            modalTitle: '',
            modalMessage: '',
            modalIcon: 'info',
            showTableOfContents: false,
            isStarting: false
        };
    }

    componentDidMount() {
        this.restoreSidebar();  // reopen sidebar when returning from assessment
        this.fetchAllStatuses();
    }

    componentWillUnmount() {
        // Do NOT restore sidebar here — it would immediately reopen it
        // right after collapseSidebar() closes it (since delegate calls unmount EPK-V2).
        // Restoration is handled by componentDidMount on re-mount after assessment.
    }

    componentDidUpdate(prevProps) {
        const prevKey = prevProps.meta?.chapterKey || prevProps.metaInfo?.chapterKey || prevProps.meta?.key || prevProps.metaInfo?.key;
        const currentKey = this.props.meta?.chapterKey || this.props.metaInfo?.chapterKey || this.props.meta?.key || this.props.metaInfo?.key;
        if (prevKey !== currentKey && currentKey) {
            this.meta = this.props.meta || this.props.metaInfo || {};
            if (this.meta.title && !this.meta.chapterTitle) {
                this.meta.chapterTitle = this.meta.title;
            }
            if (!this.meta.chapterKey && this.meta.key) {
                this.meta.chapterKey = this.meta.key;
            }
            this.setState({ isLoading: true }, () => {
                this.fetchAllStatuses();
            });
        }
    }

    showPopupModal = (message, title = 'Baseline Assessment', icon = 'info') => {
        this.setState({
            showModal: true,
            modalTitle: title,
            modalMessage: message,
            modalIcon: icon
        });
    }

    closePopupModal = () => {
        this.setState({
            showModal: false,
            modalMessage: '',
            modalTitle: '',
            modalIcon: 'info'
        });
    }

    fetchAllStatuses = () => {
        this.fetchStartTime = Date.now();
        this.fetchBaselineStatus();
        this.fetchFinishlineStatus();
        this.fetchPdfKitActivity();
    }

    hideLoadingWithDelay = () => {
        const elapsedTime = Date.now() - (this.fetchStartTime || Date.now());
        const remainingTime = 1000 - elapsedTime;
        if (remainingTime > 0) {
            setTimeout(() => {
                this.setState({ isLoading: false });
            }, remainingTime);
        } else {
            this.setState({ isLoading: false });
        }
    }

    fetchBaselineStatus = async () => {
        try {
            const chapterKey = this.meta.chapterKey || this.meta.key;
            if (!chapterKey) {
                this.hideLoadingWithDelay();
                return;
            }
            const response = await ExamPrepKitV2Api.getBaselineAssessmentStatus({ chapterKey });
            const data = (response && response.data !== undefined) ? response.data : response;
            if (data) {
                this.setState({ baselineStatus: data }, this.hideLoadingWithDelay);
            } else {
                this.hideLoadingWithDelay();
            }
        } catch (error) {
            console.error("Error fetching baseline status:", error);
            this.setState({ baselineStatus: { hasBaselineSet: false } }, this.hideLoadingWithDelay);
        }
    }

    fetchFinishlineStatus = async () => {
        try {
            const chapterKey = this.meta.chapterKey || this.meta.key;
            if (!chapterKey) {
                return;
            }
            const response = await ExamPrepKitV2Api.getFinishlineAssessmentStatus({ chapterKey });
            const data = (response && response.data !== undefined) ? response.data : response;
            if (data) {
                this.setState({ finishlineStatus: data });
            }
        } catch (error) {
            console.error("Error fetching finishline status:", error);
            this.setState({ finishlineStatus: { hasFinishlineSet: false } });
        }
    }

    fetchPdfKitActivity = async () => {
        try {
            const chapterKey = this.meta.chapterKey || this.meta.key;
            if (!chapterKey) return;
            const response = await ExamPrepKitV2Api.getPdfKitActivity({ chapterKey });
            const data = (response && response.data !== undefined) ? response.data : response;
            if (data) {
                const sectionKeys = ['revise', 'practice', 'prepareAndCaseStudy', 'cheatSheet', 'thingsToRemember', 'commonErrors', 'tipsAndTricks'];
                let completedCount = 0;
                let lastCompletedPercentage = 0;
                sectionKeys.forEach(key => {
                    if (data[key] && data[key].isCompleted) {
                        completedCount++;
                        const p = Number(data[key].percentage);
                        if (p > 0) {
                            lastCompletedPercentage = p;
                        }
                    }
                });
                const reviseProgress = completedCount === 0
                    ? 0
                    : (completedCount === sectionKeys.length
                        ? 100
                        : (lastCompletedPercentage || Math.round((completedCount / sectionKeys.length) * 100)));
                this.setState({ reviseProgress });
            }
        } catch (error) {
            console.error("Error fetching pdf kit activity:", error);
        }
    }

    goBack = () => {
        this.props.delegate &&
            this.props.delegate.hideExamPrepKitV2 &&
            this.props.delegate.hideExamPrepKitV2();
    }

    goToHomeScreen = () => {
        if (this.props.delegate && this.props.delegate.goToHomeScreen) {
            this.props.delegate.goToHomeScreen();
        } else if (this.props.history) {
            this.props.history.push('/student/home');
        }
    }

    goBackToChapter = () => {
        if (this.props.delegate && this.props.delegate.goToPrepare) {
            this.props.delegate.goToPrepare();
        } else if (this.props.history) {
            this.props.history.goBack();
        }
    }

    hideTableOfContents = () => {
        this.setState({ showTableOfContents: false });
        this.fetchAllStatuses();
    }

    hideExamPrepKit = () => {
        this.setState({ showTableOfContents: false });
        this.fetchAllStatuses();
    }

    collapseSidebar = () => {
        try {
            const layoutShared = this.layoutShared || LayoutShared.getLayout();
            if (layoutShared) {
                // Save the current sidebar open/closed state so we can restore it exactly
                this._sidebarWasHidden = layoutShared.state
                    ? layoutShared.state.hideSideBar
                    : true;
                // 1. Visually close the sidebar
                if (layoutShared.toggleSidebar) {
                    layoutShared.toggleSidebar(true);
                }
                // 2. Disable all sidebar route clicks
                if (layoutShared.setRoutesDisabled) {
                    layoutShared.setRoutesDisabled(true);
                }
            }
        } catch (e) {
            console.error('[EPK-V2] Error collapsing sidebar:', e);
        }
    }

    restoreSidebar = () => {
        try {
            const layoutShared = this.layoutShared || LayoutShared.getLayout();
            if (layoutShared) {
                // 1. Restore sidebar to the state it was in before assessment started
                if (layoutShared.toggleSidebar) {
                    layoutShared.toggleSidebar(this._sidebarWasHidden === true ? true : false);
                }
                // 2. Re-enable all sidebar route clicks
                if (layoutShared.setRoutesDisabled) {
                    layoutShared.setRoutesDisabled(false);
                }
            }
        } catch (e) {
            console.error('[EPK-V2] Error restoring sidebar:', e);
        }
    }


    goToSchoolAssessment = (data) => {
        this.collapseSidebar();
        if (this.props.delegate && this.props.delegate.goToSchoolAssessment) {
            this.props.delegate.goToSchoolAssessment(data);
        }
    }

    navigateToAssessment = (history, data) => {
        this.collapseSidebar();
        history.push(Path.CLASS_SPECIFIC_ASSESSMENT, { data });
    }

    onStepPress = async (stepKey, actionType = 'START') => {
        let history = this.props.history;
        let currentDelegate = this.props.delegate;
        while (!history && currentDelegate && currentDelegate.props) {
            history = currentDelegate.props.history;
            currentDelegate = currentDelegate.props.delegate;
        }

        const chapterInfo = {
            chapterKey: this.meta.chapterKey,
            chapterTitle: this.meta.chapterTitle,
            calledFrom: 'EXAM_PREP_KIT_V2',
            categoryThemeId: this.meta.categoryThemeId
        };

        switch (stepKey) {
            case 'BASELINE_ASSESSMENT':
                if (actionType === 'START' || actionType === 'RESUME') {
                    const { baselineStatus } = this.state;
                    if (baselineStatus && !baselineStatus.hasBaselineSet) {
                        this.showPopupModal("No Baseline assessment is assigned for this chapter yet. Please check back later.", "Baseline Assessment", "info");
                        return;
                    }
                    if (baselineStatus && baselineStatus.hasSchoolAssignment && baselineStatus.hasBaselineSet) {
                        // School Assessment path — uses Assessment/Homework flow
                        if (this.props.delegate && this.props.delegate.goToSchoolAssessment) {
                            this.goToSchoolAssessment({
                                chapterKey: this.meta.chapterKey,
                                chapterTitle: this.meta.chapterTitle,
                                categoryThemeId: this.meta.categoryThemeId,
                                calledFrom: 'EXAM_PREP_KIT_V2',
                                isFromMyHomework: true,
                                isBaseline: true,
                                homeworkActivityId: baselineStatus.homeworkActivityId,
                                homeworkId: baselineStatus.homeworkId,
                                assessmentActivityId: baselineStatus.assessmentActivityId,
                                from: 'HOMEWORK',
                                actionType: actionType,
                                isResume: actionType === 'RESUME'
                            });
                        } else {
                            console.warn('[EPK-V2] goToSchoolAssessment not available on delegate');
                        }
                    } else {
                        // Self-assessment path — uses Mock Test flow with questionSetType: 'BASELINE'
                        if (!history && (!this.props.delegate || !this.props.delegate.goToSchoolAssessment)) {
                            console.warn('[EPK-V2] No navigation handler found.');
                            return;
                        }
                        if (this.state.isStarting) return;

                        try {
                            this.setState({ isStarting: true });
                            const res = await ExamPrepKitV2Api.startBaselineAssessment({
                                chapterKey: this.meta.chapterKey,
                                chapterTitle: this.meta.chapterTitle
                            });
                            if (res && res.data) {
                                const data = res.data.data || res.data;
                                if (data && data.alreadyCompleted) {
                                    this.fetchBaselineStatus();
                                    return;
                                }
                                if (data && (data.assessmentActivityId || data.activityId || data.questionSetId)) {
                                    const assessmentData = {
                                        ...chapterInfo,
                                        isBaseline: true,
                                        from: 'MOCK_TEST',
                                        isFromMyHomework: false,
                                        assessmentActivityId: data.assessmentActivityId || data.activityId,
                                        questionSetId: data.questionSetId,
                                        isResume: data.isResumed
                                    };
                                    if (this.props.delegate && this.props.delegate.goToSchoolAssessment) {
                                        this.goToSchoolAssessment(assessmentData);
                                    } else if (history) {
                                        this.navigateToAssessment(history, assessmentData);
                                    }
                                } else if (res.data.message) {
                                    this.showPopupModal(res.data.message, "Baseline Assessment", "info");
                                }
                            }
                        } catch (err) {
                            console.error("Error starting baseline assessment", err);
                            const msg = (err.response && err.response.data && err.response.data.message)
                                || "No Baseline assessment is assigned for this chapter yet. Please check back later.";
                            this.showPopupModal(msg, "Baseline Assessment", "info");
                        } finally {
                            this.setState({ isStarting: false });
                        }
                    }
                } else if (actionType === 'VIEW_ANSWERS' || actionType === 'VIEW_REPORT') {
                    const { baselineStatus } = this.state;
                    if (baselineStatus && (baselineStatus.hasSchoolAssignment || baselineStatus.source === 'SCHOOL_ASSESSMENT')) {
                        if (this.props.delegate && this.props.delegate.goToSchoolAssessment) {
                            this.goToSchoolAssessment({
                                chapterKey: this.meta.chapterKey,
                                chapterTitle: this.meta.chapterTitle,
                                categoryThemeId: this.meta.categoryThemeId,
                                calledFrom: 'EXAM_PREP_KIT_V2',
                                isFromMyHomework: true,
                                homeworkActivityId: baselineStatus.homeworkActivityId,
                                homeworkId: baselineStatus.homeworkId,
                                assessmentActivityId: baselineStatus.assessmentActivityId,
                                from: 'HOMEWORK',
                                actionType: actionType
                            });
                        }
                    } else if (baselineStatus && baselineStatus.source === 'SELF_ASSESSMENT') {
                        if (this.props.delegate && this.props.delegate.goToSchoolAssessment) {
                            this.goToSchoolAssessment({
                                chapterKey: this.meta.chapterKey,
                                chapterTitle: this.meta.chapterTitle,
                                categoryThemeId: this.meta.categoryThemeId,
                                calledFrom: 'EXAM_PREP_KIT_V2',
                                isFromMyHomework: false,
                                isBaseline: true,
                                from: 'MOCK_TEST',
                                assessmentActivityId: baselineStatus.activityId,
                                actionType: actionType
                            });
                        } else if (history) {
                            this.navigateToAssessment(history, {
                                ...chapterInfo,
                                isBaseline: true,
                                from: 'MOCK_TEST',
                                isFromMyHomework: false,
                                assessmentActivityId: baselineStatus.activityId,
                                actionType: actionType
                            });
                        }
                    }
                }
                break;

            case 'REVISE_AND_PRACTICE':
                if (this.state.baselineStatus && this.state.baselineStatus.isCompleted) {
                    const chapterKey = this.meta.chapterKey || this.meta.key;
                    // if (chapterKey) {
                    //     ExamPrepKitV2Api.updatePdfKitActivity({ chapterKey });
                    // }
                    this.setState({ showTableOfContents: true });
                } else {
                    this.showPopupModal("Please complete the Baseline Assessment first to unlock Revise & Practice.", "Revise & Practice Locked", "locked");
                }
                break;

            case 'FINISHLINE_ASSESSMENT':
                if (actionType === 'START' || actionType === 'RESUME') {
                    const { finishlineStatus, baselineStatus, reviseProgress } = this.state;
                    const baselineCompleted = baselineStatus && baselineStatus.isCompleted;
                    // const reviseCompleted = reviseProgress === 100;

                    if (!baselineCompleted) {
                        this.showPopupModal("Please complete the Baseline Assessment first to unlock FinishLine Assessment.", "FinishLine Assessment Locked", "locked");
                        return;
                    }

                    // Step 2 must be fully completed before Step 3 unlocks
                    // if (!reviseCompleted) {
                    //     this.showPopupModal("Please complete all sections in Revise & Practice (Step 2) to unlock the FinishLine Assessment.", "FinishLine Assessment Locked", "locked");
                    //     return;
                    // }

                    if (finishlineStatus && !finishlineStatus.hasFinishlineSet) {
                        this.showPopupModal("No FinishLine assessment is assigned for this chapter yet. Please check back later.", "FinishLine Assessment", "info");
                        return;
                    }

                    if (finishlineStatus && finishlineStatus.hasSchoolAssignment && finishlineStatus.hasFinishlineSet) {
                        // School Assessment path — uses Assessment/Homework flow
                        if (this.props.delegate && this.props.delegate.goToSchoolAssessment) {
                            this.goToSchoolAssessment({
                                chapterKey: this.meta.chapterKey,
                                chapterTitle: this.meta.chapterTitle,
                                categoryThemeId: this.meta.categoryThemeId,
                                calledFrom: 'EXAM_PREP_KIT_V2',
                                isFromMyHomework: true,
                                isFinishLine: true,
                                homeworkActivityId: finishlineStatus.homeworkActivityId,
                                homeworkId: finishlineStatus.homeworkId,
                                assessmentActivityId: finishlineStatus.assessmentActivityId,
                                from: 'HOMEWORK',
                                actionType: actionType,
                                isResume: actionType === 'RESUME'
                            });
                        } else {
                            console.warn('[EPK-V2] goToSchoolAssessment not available on delegate');
                        }
                    } else {
                        // Self-assessment path — uses Mock Test flow with questionSetType: 'FINISHLINE'
                        if (!history && (!this.props.delegate || !this.props.delegate.goToSchoolAssessment)) {
                            console.warn('[EPK-V2] No navigation handler found.');
                            return;
                        }
                        if (this.state.isStarting) return;

                        try {
                            this.setState({ isStarting: true });
                            const res = await ExamPrepKitV2Api.startFinishlineAssessment({
                                chapterKey: this.meta.chapterKey,
                                chapterTitle: this.meta.chapterTitle
                            });
                            if (res && res.data) {
                                const data = res.data.data || res.data;
                                if (data && data.alreadyCompleted) {
                                    this.fetchAllStatuses();
                                    return;
                                }
                                if (data && (data.assessmentActivityId || data.activityId || data.questionSetId)) {
                                    const assessmentData = {
                                        ...chapterInfo,
                                        isFinishLine: true,
                                        from: 'MOCK_TEST',
                                        isFromMyHomework: false,
                                        assessmentActivityId: data.assessmentActivityId || data.activityId,
                                        questionSetId: data.questionSetId,
                                        isResume: data.isResumed
                                    };
                                    if (this.props.delegate && this.props.delegate.goToSchoolAssessment) {
                                        this.goToSchoolAssessment(assessmentData);
                                    } else if (history) {
                                        this.navigateToAssessment(history, assessmentData);
                                    }
                                } else if (res.data.message) {
                                    this.showPopupModal(res.data.message, "FinishLine Assessment", "info");
                                }
                            }
                        } catch (err) {
                            console.error("Error starting finishline assessment", err);
                            const msg = (err.response && err.response.data && err.response.data.message)
                                || "No FinishLine assessment is assigned for this chapter yet. Please check back later.";
                            this.showPopupModal(msg, "FinishLine Assessment", "info");
                        } finally {
                            this.setState({ isStarting: false });
                        }
                    }
                } else if (actionType === 'VIEW_ANSWERS' || actionType === 'VIEW_REPORT') {
                    const { finishlineStatus } = this.state;
                    if (finishlineStatus && (finishlineStatus.hasSchoolAssignment || finishlineStatus.source === 'SCHOOL_ASSESSMENT')) {
                        if (this.props.delegate && this.props.delegate.goToSchoolAssessment) {
                            this.goToSchoolAssessment({
                                chapterKey: this.meta.chapterKey,
                                chapterTitle: this.meta.chapterTitle,
                                categoryThemeId: this.meta.categoryThemeId,
                                calledFrom: 'EXAM_PREP_KIT_V2',
                                isFromMyHomework: true,
                                isFinishLine: true,
                                homeworkActivityId: finishlineStatus.homeworkActivityId,
                                homeworkId: finishlineStatus.homeworkId,
                                assessmentActivityId: finishlineStatus.assessmentActivityId,
                                from: 'HOMEWORK',
                                actionType: actionType
                            });
                        }
                    } else if (finishlineStatus && finishlineStatus.source === 'SELF_ASSESSMENT') {
                        if (this.props.delegate && this.props.delegate.goToSchoolAssessment) {
                            this.goToSchoolAssessment({
                                chapterKey: this.meta.chapterKey,
                                chapterTitle: this.meta.chapterTitle,
                                categoryThemeId: this.meta.categoryThemeId,
                                calledFrom: 'EXAM_PREP_KIT_V2',
                                isFromMyHomework: false,
                                isFinishLine: true,
                                from: 'MOCK_TEST',
                                assessmentActivityId: finishlineStatus.activityId,
                                actionType: actionType
                            });
                        } else if (history) {
                            this.navigateToAssessment(history, {
                                ...chapterInfo,
                                isFinishLine: true,
                                from: 'MOCK_TEST',
                                isFromMyHomework: false,
                                assessmentActivityId: finishlineStatus.activityId,
                                actionType: actionType
                            });
                        }
                    }
                }
                break;

            default:
                break;
        }
    }

    // ── Step definitions ────────────────────────────────────────

    getSteps() {
        const { baselineStatus, finishlineStatus, isLoading, reviseProgress } = this.state;

        let baselineCompleted = false;
        let baselineStatusLabel = 'Start Here';
        let baselineCtaLabel = 'Start Now';

        let baselineStatusChip = 'chip--new';

        if (baselineStatus) {
            if (baselineStatus.hasBaselineSet === false) {
                baselineStatusLabel = 'Not Available';
                baselineCtaLabel = 'Not Available';
                baselineStatusChip = 'chip--locked';
            } else if (baselineStatus.isCompleted) {
                baselineCompleted = true;
                baselineStatusLabel = 'Completed';
                baselineCtaLabel = 'View Answers';
                baselineStatusChip = 'chip--active';
            } else if (baselineStatus.isInProgress) {
                baselineStatusLabel = 'In Progress';
                baselineCtaLabel = 'Resume';
            } else if (baselineStatus.hasSchoolAssignment) {
                baselineStatusLabel = 'Start Here';
                baselineCtaLabel = 'Start Now';
            }
        }

        let finishlineCompleted = false;
        let finishlineStatusLabel = 'Locked';
        let finishlineCtaLabel = 'Take Test';
        // Step 3 requires BOTH Step 1 (Baseline) AND Step 2 (Revise & Practice = 100%) to be done
        const reviseCompleted = true;
        let finishlineLocked = !baselineCompleted || !reviseCompleted;
        let finishlineStatusChip = 'chip--locked';

        if (baselineCompleted && reviseCompleted) {
            finishlineStatusLabel = 'Start Here';
            finishlineCtaLabel = 'Take Test';
            finishlineStatusChip = 'chip--new';
            finishlineLocked = false;

            if (finishlineStatus) {
                if (finishlineStatus.hasFinishlineSet === false) {
                    finishlineStatusLabel = 'Not Available';
                    finishlineCtaLabel = 'Not Available';
                    finishlineStatusChip = 'chip--locked';
                } else if (finishlineStatus.isCompleted) {
                    finishlineCompleted = true;
                    finishlineStatusLabel = 'Completed';
                    finishlineCtaLabel = 'View Answers';
                    finishlineStatusChip = 'chip--active';
                } else if (finishlineStatus.isInProgress) {
                    finishlineStatusLabel = 'In Progress';
                    finishlineCtaLabel = 'Resume';
                    finishlineStatusChip = 'chip--new';
                } else if (finishlineStatus.hasSchoolAssignment) {
                    finishlineStatusLabel = 'Start Here';
                    finishlineCtaLabel = 'Start Now';
                    finishlineStatusChip = 'chip--new';
                }
            }
        }

        return [
            {
                stepNumber: 1,
                stepKey: 'BASELINE_ASSESSMENT',
                title: 'Baseline Assessment',
                description: 'Take a quick diagnostic test to identify your strengths and knowledge gaps before you begin.',
                ctaLabel: baselineCtaLabel,
                statusLabel: isLoading ? 'Loading...' : baselineStatusLabel,
                statusChip: isLoading ? 'chip--locked' : baselineStatusChip,
                isLocked: false,
                isCompleted: baselineCompleted,
                isInProgress: !baselineCompleted && baselineStatus && baselineStatus.isInProgress,
            },
            {
                stepNumber: 2,
                stepKey: 'REVISE_AND_PRACTICE',
                title: 'Revise & Practice',
                description: 'Work through concept summaries, cheat sheets, and practice questions at your own pace.',
                ctaLabel: !baselineCompleted ? 'Start Revising' : (reviseProgress === 100 ? 'Revise Again' : (reviseProgress > 0 ? 'Resume' : 'Start Revising')),
                statusLabel: !baselineCompleted ? 'Locked' : (reviseProgress === 100 ? 'Completed' : (reviseProgress > 0 ? 'In Progress' : 'Start Here')),
                statusChip: !baselineCompleted ? 'chip--locked' : (reviseProgress === 100 ? 'chip--active' : 'chip--new'),
                isLocked: !baselineCompleted,
                isCompleted: reviseCompleted,
                isInProgress: reviseProgress > 0 && !reviseCompleted,
                progress: reviseProgress,
            },
            {
                stepNumber: 3,
                stepKey: 'FINISHLINE_ASSESSMENT',
                title: 'FinishLine Assessment',
                description: 'Take the final full-length mock exam and see how much you have improved.',
                ctaLabel: finishlineCtaLabel,
                statusLabel: isLoading ? 'Loading...' : finishlineStatusLabel,
                statusChip: isLoading ? 'chip--locked' : finishlineStatusChip,
                isLocked: finishlineLocked,
                isCompleted: finishlineCompleted,
                isInProgress: !finishlineCompleted && !finishlineLocked && finishlineStatus && finishlineStatus.isInProgress,
                // tooltip shown when locked, explains which step is missing
                lockedReason: !baselineCompleted ? 'Complete Step 1 first' : (!reviseCompleted ? 'Complete Step 2 first' : ''),
            },
        ];
    }

    // ── Render ───────────────────────────────────────────────────

    renderBreadcrumb() {
        return (
            <div className='breadcrumb mt-2 bg-transparent pl-0'>
                <span className="home cursor-pointer" onClick={this.goToHomeScreen} >{`Home >`}</span>
                <span className='title-1 pl-2 cursor-pointer' onClick={this.goBackToChapter}>{`${this.meta.chapterTitle || 'Chapter'} >`}</span>
                <span className='title-1 pl-2 active'>{`Exam Prep Kit`}</span>
            </div>
        );
    }

    renderStep(step, isLast) {
        const { stepNumber: n, stepKey, title, description, ctaLabel, statusLabel, statusChip, isLocked, isCompleted, isInProgress, progress } = step;

        return (
            <div className={`epk-step-row${isLast ? ' epk-step-row--last' : ''}`} key={stepKey}>
                {/* Left rail: badge + connecting line */}
                <div className={`epk-rail${isCompleted ? ' epk-rail--completed' : ''}`}>
                    <div className={`epk-badge epk-badge--${n}${isLocked ? ' epk-badge--locked' : ''}`}>
                        {isCompleted ? '✓' : n}
                    </div>
                </div>

                {/* Card */}
                <div className="epk-card-wrap">
                    <div
                        className={`epk-card epk-card--${n}${isLocked ? ' epk-card--locked' : ''}`}
                    >
                        {/* Flex container for text to wrap together nicely on mobile */}
                        <div className="epk-card-header-mobile">
                            {/* Text content */}
                            <div className="epk-card-content">
                                <div className="epk-step-meta">
                                    <span className="epk-step-label">Step {n}</span>
                                    <span className={`epk-status-chip ${statusChip}`}>{statusLabel}</span>
                                    {n === 2 && !isLocked && (
                                        <span className="epk-percentage-chip">{`${progress || 0}%`}</span>
                                    )}
                                </div>
                                <div className="epk-card-title">{title}</div>
                                <p className="epk-card-desc d-none d-md-block">{description}</p>
                            </div>
                        </div>
                        <p className="epk-card-desc d-block d-md-none mt-2">{description}</p>

                        {/* CTA */}
                        <div className="epk-card-action">
                            {isCompleted && (n === 1 || n === 3) ? (
                                <div className="epk-dual-actions">
                                    <button
                                        className="epk-cta-btn epk-cta-btn--secondary"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            this.onStepPress(stepKey, 'VIEW_ANSWERS');
                                        }}
                                    >
                                        View Answers
                                    </button>
                                    <button
                                        className="epk-cta-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            this.onStepPress(stepKey, 'VIEW_REPORT');
                                        }}
                                    >
                                        View Report
                                    </button>
                                </div>
                            ) : (
                                <button
                                    className="epk-cta-btn"
                                    id={`epk-cta-${n}`}
                                    disabled={isLocked}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (!isLocked) this.onStepPress(stepKey, isInProgress ? 'RESUME' : 'START');
                                    }}
                                >
                                    {isLocked ? 'Locked 🔒' : ctaLabel}
                                    {!isLocked && <span className="epk-cta-arrow">›</span>}
                                </button>
                            )}
                        </div>

                    </div>
                    {isLocked && (
                        <div className="epk-locked-tooltip">
                            {step.lockedReason || 'Complete the previous step to unlock'}
                        </div>
                    )}
                </div>

            </div>
        );
    }

    renderModalPopup() {
        if (!this.state.showModal) return null;

        const iconMap = {
            info: '📋',
            warning: '⚠️',
            locked: '🔒',
            success: '✅'
        };

        const iconDisplay = iconMap[this.state.modalIcon] || '📋';

        return (
            <Modal
                className="epk-popup-modal"
                isOpen={this.state.showModal}
                toggle={this.closePopupModal}
                centered
            >
                <div className="epk-modal-content">
                    <div className="epk-modal-header d-flex justify-content-between align-items-center">
                        <div className="epk-modal-title">{this.state.modalTitle || 'Notice'}</div>
                        <button
                            type="button"
                            className="epk-modal-close-btn cursor-pointer"
                            onClick={this.closePopupModal}
                            aria-label="Close"
                        >
                            ✕
                        </button>
                    </div>
                    <ModalBody className="epk-modal-body text-center">
                        <div className="epk-modal-icon-wrap">
                            <span className="epk-modal-badge-icon">{iconDisplay}</span>
                        </div>
                        <p className="epk-modal-message-text">{this.state.modalMessage}</p>
                        <div className="d-flex justify-content-center mt-4">
                            <button
                                className="epk-modal-btn"
                                onClick={this.closePopupModal}
                            >
                                Got it
                            </button>
                        </div>
                    </ModalBody>
                </div>
            </Modal>
        );
    }

    render() {
        if (this.state.isLoading) {
            return <Loading />;
        }

        if (this.state.showTableOfContents) {
            return (
                <TableOfContent
                    delegate={this}
                    metaInfo={{
                        ...this.meta,
                        chapterKey: this.meta.chapterKey || this.meta.key,
                        chapterTitle: this.meta.chapterTitle || this.meta.title,
                        title: this.meta.chapterTitle || this.meta.title,
                        categoryThemeId: this.meta.categoryThemeId,
                        subjectId: this.meta.subjectId || this.userInfo?.subjectId || Constants.MATHS_SUBJECTID,
                        from: 'EXAM_PREP_KIT_V2',
                        baseLineAssessmentActivityId: this.state.baselineStatus ? (this.state.baselineStatus.assessmentActivityId || this.state.baselineStatus.activityId || '') : ''
                    }}
                    showPrepKitChapterList={this.props.showPrepKitChapterList}
                />
            );
        }

        const steps = this.getSteps();
        const completedSteps = steps.filter(s => s.isCompleted).length;
        const totalSteps = steps.length;
        const progressPercentage = Math.round((completedSteps / totalSteps) * 100);

        return (
            <div className="exam-prep-kit-v2">
                {/* Breadcrumb */}
                {this.renderBreadcrumb()}

                <div className="epk-container">
                    {/* Page header */}
                    <div className="epk-page-header">
                        <div className="d-flex flex-column align-items-center text-center">
                            <h1 className="epk-page-title">
                                <span className="epk-highlight">Exam Prep Kit</span>
                            </h1>
                            <p className="epk-page-subtitle mb-4">
                                Your 3-step path to ace {this.meta.chapterTitle ? `"${this.meta.chapterTitle}"` : 'your exam'}.
                            </p>

                            {/* Progress Indicator */}
                            <div className="epk-progress-indicator text-center mx-auto" style={{ minWidth: '220px' }}>
                                <div className="epk-progress-text">
                                    <strong>{progressPercentage}%</strong> completed
                                </div>
                                <div className="epk-progress-bar-wrap">
                                    <div className="epk-progress-fill" style={{ width: `${progressPercentage}%` }}></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Timeline */}
                    <div className="epk-timeline" role="list">
                        {steps.map((step, idx) =>
                            this.renderStep(step, idx === steps.length - 1)
                        )}
                    </div>
                </div>

                {/* Popup Window */}
                {this.renderModalPopup()}
            </div>
        );
    }
}

export default ExamPrepKitV2;
